@if (loading) {
  <section class="gallery-status">
    <div class="status-card">
      <i class="fas fa-spinner fa-spin status-icon"></i>
      <h3>Loading photos…</h3>
    </div>
  </section>
} @else if (loadError) {
  <section class="gallery-status">
    <div class="status-card">
      <i class="fas fa-triangle-exclamation status-icon"></i>
      <h3>We couldn't load this gallery</h3>
      <p>Please check your connection and try again. If the problem continues, contact your project team.</p>
    </div>
  </section>
} @else if (expired) {
  <section class="gallery-status">
    <div class="status-card">
      <i class="fas fa-lock status-icon"></i>
      <h3>This photo gallery is no longer available</h3>
      <p>The secure link has expired. Please contact your project team if you still need access.</p>
    </div>
  </section>
} @else if (gallery) {

  <header class="pg-topbar">
    <div class="pg-topbar-inner">
      <span class="pg-brand"><i class="fas fa-images"></i> Photo Gallery</span>
      <!-- A Leadman tapping the SMS on iOS lands here in Safari with no way back: the page came
           from Messages, so Safari's own Back is disabled. Hand our own people back to the app.
           #20260813-6: gated on canOpenInApp, not "is internal" — that also matched the Account
           Manager, who works in the web app and has no mobile app to open. Never shown to a
           client either. -->
      @if (gallery.canOpenInApp) {
        <!-- Feedback #CM24: a custom-scheme link does NOTHING when the app isn't installed — no
             error, no navigation — so the button looked broken. Drive it from code and say what
             happened if nothing takes it. -->
        <button type="button" class="pg-app-link" (click)="openInApp()">
          <i class="fas fa-mobile-alt"></i> Open in the app
        </button>
      } @else {
        <span class="pg-pill">Active</span>
      }
    </div>
    @if (appOpenFailed) {
      <div class="pg-app-note">
        <i class="fas fa-info-circle"></i>
        <span>
          The Leadman app didn't open — it may not be installed on this device.
          You can carry on viewing and commenting here in the browser.
        </span>
        <button type="button" class="pg-app-note-close" (click)="appOpenFailed = false" aria-label="Dismiss">&times;</button>
      </div>
    }
  </header>

  <section class="pg-wrap">

    <!-- Service Order summary -->
    <div class="pg-so-card">
      <div class="pg-so-name">{{ gallery.exhibitorName || gallery.soNumber }}</div>
      <div class="pg-so-sub">
        <span>SO {{ gallery.soNumber }}</span>
        @if (allPhotos.length) {
          <span class="pg-dot">•</span><span>{{ allPhotos.length }} photo{{ allPhotos.length === 1 ? '' : 's' }}</span>
        }
      </div>
      <div class="pg-tags">
        @if (gallery.showName) {
          <span class="pg-tag"><i class="fas fa-building"></i>{{ gallery.showName }}</span>
        }
        @if (gallery.boothNumber) {
          <span class="pg-tag"><i class="fas fa-location-dot"></i>Booth {{ gallery.boothNumber }}</span>
        }
      </div>
    </div>

    @if (allPhotos.length === 0) {
      <div class="pg-empty">
        <i class="fas fa-images"></i>
        <p>No photos have been shared yet</p>
      </div>
    }

    <!-- Fanfold: one card per job day -->
    @for (day of gallery.days; track day.jobDay) {
      <div class="pg-day-card">
        <div class="pg-day-head">
          <span class="pg-day-title">{{ day.jobDayDisplay || 'Photos' }}</span>
          <span class="pg-day-count">{{ day.photos.length }} photo{{ day.photos.length === 1 ? '' : 's' }}</span>
        </div>
        <div class="pg-grid">
          @for (photo of day.photos; track photo.galleryPhotoId) {
            <div class="pg-thumb" (click)="openPhoto(photo)">
              <img [src]="imageUrl(photo)" loading="lazy" [alt]="photo.fileName" />
              @if (photo.commentCount > 0) {
                <span class="pg-thumb-badge"><i class="fas fa-comment"></i> {{ photo.commentCount }}</span>
              }
            </div>
          }
        </div>
      </div>
    }
  </section>

  <!-- Lightbox: image + metadata + activity -->
  @if (lightboxOpen && activePhoto) {
    <div class="pg-lightbox" (touchstart)="onTouchStart($event)" (touchend)="onTouchEnd($event)">

      <div class="pg-lb-bar">
        <span class="pg-lb-count">{{ activeIndex + 1 }} of {{ allPhotos.length }}</span>
        <!-- Feedback #31: fa-xmark is a Font Awesome 6 name but this app renders FA 5.13.1, so the
             glyph resolved to nothing and iOS users saw a blank circle with no way to tell it was
             the exit. Use the FA5 name AND label it — iOS has no system Back button, so this is the
             only way out of the lightbox. -->
        <button class="pg-lb-close" (click)="closeLightbox()" aria-label="Close photo">
          <i class="fas fa-times" aria-hidden="true"></i><span class="pg-lb-close-text">Close</span>
        </button>
      </div>

      <div class="pg-stage">
        @if (activeIndex > 0) {
          <button class="pg-nav prev" (click)="prev()" aria-label="Previous"><i class="fas fa-chevron-left"></i></button>
        }
        <img [src]="imageUrl(activePhoto)" [alt]="activePhoto.fileName" />
        @if (activeIndex < allPhotos.length - 1) {
          <button class="pg-nav next" (click)="next()" aria-label="Next"><i class="fas fa-chevron-right"></i></button>
        }
      </div>

      <div class="pg-sheet">
        <div class="pg-meta-row">
          <div class="pg-meta-main">
            <div class="pg-meta-id">{{ activePhoto.fileName }}</div>
            <div class="pg-meta-sub">
              @if (activePhoto.uploadedUtc) { <span>Uploaded {{ activePhoto.uploadedUtc | date:'MMM d, h:mm a' }}</span> }
              @if (activePhoto.uploadedByName) { <span> · {{ activePhoto.uploadedByName }}</span> }
            </div>
          </div>
          @if (photoFaces(activePhoto).length) {
            <div class="pg-faces">
              @for (n of photoFaces(activePhoto); track n) {
                <span class="pg-face">{{ n.charAt(0) }}</span>
              }
              @if (photoFacesOverflow(activePhoto) > 0) {
                <span class="pg-face more">+{{ photoFacesOverflow(activePhoto) }}</span>
              }
            </div>
          }
        </div>

        <div class="pg-tags">
          @if (activePhotoCategory) {
            <span class="pg-tag"><i class="fas fa-tag"></i>{{ activePhotoCategory }}</span>
          }
          @if (gallery.boothNumber) {
            <span class="pg-tag"><i class="fas fa-location-dot"></i>Booth {{ gallery.boothNumber }}</span>
          }
        </div>

        <div class="pg-activity-title">Activity</div>

        @if (loadingComments) { <div class="pg-muted">Loading…</div> }

        @for (c of comments; track c.id) {
          <div class="pg-comment" [class.client]="c.authorType === 'client'">
            <span class="pg-avatar" [class.client]="c.authorType === 'client'">{{ (c.authorDisplayName || '?').charAt(0) }}</span>
            <div class="pg-comment-body">
              <div class="pg-comment-head">
                <span class="pg-comment-name">{{ c.authorDisplayName }}</span>
                <span class="pg-comment-time">{{ c.dateCreated | date:'shortTime' }}</span>
              </div>
              <div class="pg-comment-text">{{ c.commentText }}</div>
            </div>
          </div>
        }

        @if (comments.length === 0 && !loadingComments) {
          <div class="pg-muted">No comments yet. Add the first one below.</div>
        }

        <div class="pg-composer">
          @if (gallery.commenterName) {
            <div class="pg-commenting-as">Commenting as <strong>{{ gallery.commenterName }}</strong></div>
          }
          <div class="pg-composer-row">
            <textarea #ci rows="1" placeholder="Add a comment…"></textarea>
            <button class="pg-send" (click)="postComment(ci)" [disabled]="posting" aria-label="Send">
              <i class="fas fa-paper-plane"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  }
}
